import React, { useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';

import { Form, Formik, useFormikContext } from 'formik';
import * as Yup from 'yup';

import { Editor } from 'react-draft-wysiwyg';
import {
  EditorState,
  convertToRaw,
  convertFromHTML,
  ContentState,
} from 'draft-js';
import draftToHtml from 'draftjs-to-html';

import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';
import Select from '../../../components/forms/Select';
import { Input } from '../../../components/forms';
import InputImage from '../../../components/forms/Input/inputImage';
import InputWrapper from '../../../components/forms/Input/inputWrapper';
import Button from '../../../components/common/Button';
import Alert from '../../../components/common/Alert';
import InputFile from '../../../components/forms/Input/inputFile';
import PillarDropDown from './PillarDropDown';
import {
  isYoutubeUrl,
  normalizeYoutubeEmbedUrl,
} from '../../../utils/youtube';
import { createWorkshopSlug } from '../utils/workshopForm';

const ALLOWED_CHAR = 160;
const ALLOWED_DESCRIPTION_CHAR = 300;
const SLUG_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

const WorkshopSlugSync = () => {
  const { values, setFieldValue } = useFormikContext();
  const previousTitleRef = useRef(values.title || '');

  useEffect(() => {
    const previousGeneratedSlug = createWorkshopSlug(previousTitleRef.current);
    const currentSlug = values.slug || '';
    const shouldSyncSlug = !currentSlug || currentSlug === previousGeneratedSlug;

    if (shouldSyncSlug) {
      const nextSlug = createWorkshopSlug(values.title);
      if (nextSlug !== currentSlug) {
        setFieldValue('slug', nextSlug, false);
      }
    }

    previousTitleRef.current = values.title || '';
  }, [setFieldValue, values.title, values.slug]);

  return null;
};

const handleRecordingLinkPaste = (event, setFieldValue) => {
  const pastedValue = event.clipboardData.getData('text');
  const normalizedValue = normalizeYoutubeEmbedUrl(pastedValue);

  if (normalizedValue !== pastedValue) {
    event.preventDefault();
    setFieldValue('recordingLink', normalizedValue);
  }
};

const handleRecordingLinkBlur = (event, formikProps) => {
  formikProps.setFieldTouched('recordingLink', true);
  formikProps.setFieldValue(
    'recordingLink',
    normalizeYoutubeEmbedUrl(event.target.value)
  );
};

const getInitialPillar = (pillar) => {
  if (!pillar) {
    return {};
  }

  return typeof pillar === 'object' ? pillar : { id: pillar };
};

const getEditorHtml = (editorState) =>
  draftToHtml(convertToRaw(editorState.getCurrentContent()));

const WorkshopInfoForm = ({
  workShopData,
  onNext,
  setWorkShopData,
  pillars = [],
  isLoadingPillars = false,
  mode = 'create',
}) => {
  const history = useHistory();
  const fontSizeVisible = true;
  const [selectedPillar, setSelectedPillar] = useState(
    getInitialPillar(workShopData.pillar)
  );
  const defaultContent = workShopData.description || '';
  const blockFromHTML = convertFromHTML(defaultContent);
  const content = ContentState.createFromBlockArray(blockFromHTML);
  const [textEditor, setTextEditor] = useState(
    EditorState.createWithContent(content)
  );

  useEffect(() => {
    if (selectedPillar?.id && !selectedPillar?.name) {
      const pillar = pillars.find(
        (option) => String(option.id) === String(selectedPillar.id)
      );

      if (pillar) {
        setSelectedPillar(pillar);
      }
    }
  }, [pillars, selectedPillar]);

  const onSubmit = (values) => {
    setWorkShopData({
      ...workShopData,
      ...values,
      slug: createWorkshopSlug(values.slug || values.title),
      description: getEditorHtml(textEditor),
      recordingLink: normalizeYoutubeEmbedUrl(values.recordingLink),
      workshopLink: mode === 'create' ? '' : values.workshopLink || '',
      pillar: selectedPillar,
    });
    onNext();
  };

  const textFormSchema = Yup.object().shape({
    title: Yup.string()
      .max(
        ALLOWED_CHAR,
        `El largo máximo para el título son de ${ALLOWED_CHAR} caracteres`
      )
      .trim()
      .required('Debes ingresar un titulo para el taller'),
    slug: Yup.string()
      .required('Debes ingresar un slug para el taller')
      .matches(
        SLUG_PATTERN,
        'Usa solo minúsculas, números y guiones, sin espacios'
      ),
    description: Yup.string()
      .max(
        ALLOWED_DESCRIPTION_CHAR,
        `El largo máximo para la descripción son de ${ALLOWED_DESCRIPTION_CHAR} caracteres`
      )
      .trim()
      .required('Debes ingresar una descripción para el taller'),
    receptor: Yup.string()
      .test(
        'valid-receptor',
        'Debes seleccionar un receptor válido',
        (value) => {
          return value === 'mentor' || value === 'mentee' || value === 'both';
        }
      )
      .required('Debes seleccionar un receptor para el taller'),
    estimatedTime: Yup.string()
      .trim()
      .required('Debes ingresar la duración del taller'),
    recordingLink: Yup.string()
      .trim()
      .required('Debes ingresar un link o iframe de YouTube')
      .test(
        'youtube-url',
        'Debes ingresar un link público o iframe válido de YouTube',
        isYoutubeUrl
      ),
    image: Yup.string().required('Debes ingresar una imágen válida'),
    file: Yup.string().nullable(),
  });

  const toolbarOptions = {
    options: ['inline', 'fontSize', 'textAlign'],
    inline: {
      options: ['bold', 'italic', 'underline'],
    },
    fontSize: {
      className: 'toolbar__fontSize',
      options: [12, 14, 16, 18, 24, 30, 36],
      visible: fontSizeVisible,
    },
    textAlign: {
      className: 'toolbar__textAlign',
      options: ['left', 'center', 'right'],
    },
  };

  const returnWorkshopTable = () => {
    history.push('/talleres');
  };

  return (
    <div className="wizard__form wizard__form--full workshop-form">
      <Formik
        onSubmit={onSubmit}
        initialValues={workShopData}
        validationSchema={textFormSchema}
      >
        {(props) => {
          const showSlugWarning =
            mode === 'edit' &&
            workShopData.status === 'active' &&
            workShopData.slug &&
            props.values.slug &&
            props.values.slug !== workShopData.slug;

          return (
            <Form>
              <WorkshopSlugSync />
              <div className="workshop-form__content">
                <section className="workshop-form__section">
                  <div className="workshop-form__section-header">
                    <h3>Información del taller</h3>
                  </div>
                  <div className="workshop-form__grid">
                    <div className="workshop-form__field workshop-form__field--wide">
                      <InputWrapper
                        variant="full"
                        id="title"
                        label="Nombre del taller"
                      >
                        <Input
                          id="title"
                          label="Ingresa un texto breve"
                          name="title"
                          type="text"
                          max={160}
                        />
                      </InputWrapper>
                    </div>

                    <div className="workshop-form__field">
                      <InputWrapper
                        variant="full"
                        id="slug"
                        label="Slug"
                      >
                        <Input
                          id="slug"
                          label="slug-del-taller"
                          name="slug"
                          type="text"
                          max={180}
                        />
                      </InputWrapper>
                      {showSlugWarning ? (
                        <Alert type="warning">
                          Cambiar el slug modifica la URL pública. Se creará una redirección 301 desde el slug anterior.
                        </Alert>
                      ) : null}
                    </div>

                    <div className="workshop-form__field">
                      <InputWrapper
                        variant="full"
                        id="estimatedTime"
                        name="estimatedTime"
                        label="Duración"
                      >
                        <Input
                          id="estimatedTime"
                          label="Ej: 1 hora"
                          name="estimatedTime"
                          type="text"
                          max={80}
                        />
                      </InputWrapper>
                    </div>

                    <div className="workshop-form__field">
                      <InputWrapper
                        variant="full"
                        id="receptor"
                        name="receptor"
                        label="Usuario receptor"
                      >
                        <Select
                          label=""
                          name="receptor"
                          options={[]}
                          advancedOptions={[
                            { id: 'mentee', value: 'Mentee' },
                            { id: 'mentor', value: 'Mentor' },
                            { id: 'both', value: 'Ambos' },
                          ]}
                        />
                      </InputWrapper>
                    </div>

                    {!isLoadingPillars && (
                      <div className="workshop-form__field">
                        <InputWrapper
                          variant="full"
                          id="pillar"
                          name="pillar"
                          label="Pilar"
                        >
                          <PillarDropDown
                            name="pillar"
                            setFieldValue={props.setFieldValue}
                            setFieldTouched={props.setFieldTouched}
                            onClick={setSelectedPillar}
                            selectedOption={selectedPillar}
                            options={pillars}
                          />
                        </InputWrapper>
                      </div>
                    )}

                    <div className="workshop-form__field">
                      <InputWrapper
                        variant="full"
                        id="recordingLink"
                        name="recordingLink"
                        label="Link o iframe de YouTube"
                      >
                        <Input
                          id="recordingLink"
                          label="Pega URL o iframe"
                          name="recordingLink"
                          type="text"
                          onBlur={(event) =>
                            handleRecordingLinkBlur(event, props)
                          }
                          onPaste={(event) =>
                            handleRecordingLinkPaste(
                              event,
                              props.setFieldValue
                            )
                          }
                        />
                      </InputWrapper>
                    </div>
                  </div>
                </section>

                <section className="workshop-form__section">
                  <div className="workshop-form__section-header">
                    <h3>Contenido</h3>
                  </div>
                  <InputWrapper
                    variant="full"
                    id="description"
                    label="Descripción resumida"
                  >
                    <Editor
                      id="description"
                      name="description"
                      wrapperClassName="editor__wrapper workshop-form__editor"
                      toolbarClassName="editor__toolbar"
                      editorState={textEditor}
                      onEditorStateChange={(editorState) => {
                        setTextEditor(editorState);
                        props.setFieldValue(
                          'description',
                          getEditorHtml(editorState),
                          false
                        );
                      }}
                      onBlur={() => {
                        props.setFieldTouched('description', true);
                        props.setFieldValue(
                          'description',
                          getEditorHtml(textEditor)
                        );
                      }}
                      toolbar={toolbarOptions}
                    />
                    <div className="editor__error">
                      {props.touched.description &&
                        props.errors.description && (
                          <span className="error-message">
                            {props.errors.description}
                          </span>
                        )}
                      <span className="char-counter">
                        {textEditor.getCurrentContent().getPlainText('').length}
                        /{ALLOWED_DESCRIPTION_CHAR}
                      </span>
                    </div>
                  </InputWrapper>
                </section>

                <section className="workshop-form__section">
                  <div className="workshop-form__section-header">
                    <h3>Archivos</h3>
                  </div>
                  <div className="workshop-form__grid">
                    <div className="workshop-form__field">
                      <InputWrapper
                        variant="full"
                        label="Imagen del taller"
                        description="443x263px JPG o PNG. Máximo 5MB."
                        id="image"
                      >
                        <InputImage
                          name="image"
                          id="image"
                          allowedExts={['image/png', 'image/jpg', 'image/jpeg']}
                        />
                      </InputWrapper>
                    </div>

                    <div className="workshop-form__field">
                      <InputWrapper
                        variant="full"
                        label="Archivos complementarios"
                        description="PDF, PPT, Excel o Word. Máximo 10MB."
                        id="file"
                      >
                        <InputFile
                          name="file"
                          id="file"
                          accept=".pdf,.ppt,.pptx,.xls,.xlsx,.doc,.docx"
                          allowedExts={[
                            'application/pdf',
                            'application/vnd.ms-powerpoint',
                            'application/vnd.openxmlformats-officedocument.presentationml.presentation',
                            'application/vnd.ms-excel',
                            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                            'application/msword',
                            'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                          ]}
                        />
                      </InputWrapper>
                    </div>
                  </div>
                </section>

                <div className="wizard__action workshop-form__actions">
                  <Button secondary onClick={returnWorkshopTable}>
                    Volver
                  </Button>
                  <Button type="submit">Siguiente</Button>
                </div>
              </div>
            </Form>
          );
        }}
      </Formik>
    </div>
  );
};

export default WorkshopInfoForm;
